import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { UploadCloud, Trash2, CheckCircle2, Image as ImageIcon } from "lucide-react";
import { apiClient, API, formatApiError } from "../../lib/api";

const BACKEND_BASE = API.replace(/\/api$/, "");

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
        return iso;
    }
}

export const HeroSettings = () => {
    const [images, setImages] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [originalUrl, setOriginalUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const fetchGallery = useCallback(async () => {
        try {
            const { data } = await apiClient.get("/admin/hero-images");
            setImages(data.items || []);
            setActiveId(data.active_image_id || null);
            setOriginalUrl(data.original_url);
        } catch (err) {
            if (err?.response?.status !== 401) {
                toast.error("Galerie konnte nicht geladen werden.");
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchGallery();
    }, [fetchGallery]);

    const onFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
            toast.error("Nur JPG, PNG oder WEBP erlaubt.");
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast.error("Das Bild darf maximal 10 MB groß sein.");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await apiClient.post("/admin/hero-image", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Bild hochgeladen und als Startbild aktiviert.");
            fetchGallery();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Upload fehlgeschlagen.");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const activateImage = async (id) => {
        try {
            await apiClient.post(`/admin/hero-images/${id}/activate`);
            toast.success("Startbild aktiviert – sofort auf der Website sichtbar.");
            fetchGallery();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Aktivierung fehlgeschlagen.");
        }
    };

    const activateOriginal = async () => {
        try {
            await apiClient.delete("/admin/hero-image");
            toast.success("Original-Banner aktiviert.");
            fetchGallery();
        } catch (err) {
            toast.error("Zurücksetzen fehlgeschlagen.");
        }
    };

    const deleteImage = async (img) => {
        if (!window.confirm("Dieses Bild endgültig aus dem Archiv löschen?")) return;
        try {
            const { data } = await apiClient.delete(`/admin/hero-images/${img.id}`);
            toast.success(
                data.reset_to_original
                    ? "Bild gelöscht – Original-Banner ist wieder aktiv."
                    : "Bild aus dem Archiv gelöscht."
            );
            fetchGallery();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Löschen fehlgeschlagen.");
        }
    };

    const ActiveBadge = () => (
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-emerald-600 text-white text-xs font-medium px-2.5 py-1 rounded-full shadow">
            <CheckCircle2 size={13} /> Aktiv
        </span>
    );

    return (
        <div data-testid="hero-settings" className="space-y-6">
            <div className="bg-white border border-moebus-border rounded-lg p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                        <h2 className="font-heading font-semibold text-lg text-moebus-deep">
                            Startbild-Galerie
                        </h2>
                        <p className="text-sm text-slate-500 mt-1">
                            Alle hochgeladenen Bilder bleiben im Archiv und können jederzeit wieder aktiviert
                            werden. Empfohlen: Querformat, mind. 1200 px breit (JPG, PNG, WEBP, max. 10 MB).
                        </p>
                    </div>
                    <input
                        ref={fileRef}
                        data-testid="hero-file-input"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={onFileChange}
                        className="hidden"
                    />
                    <button
                        data-testid="hero-upload-btn"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        className="shrink-0 inline-flex items-center gap-2 bg-moebus-green hover:bg-emerald-700 text-white font-medium px-4 py-2.5 rounded-md text-sm disabled:opacity-60"
                    >
                        <UploadCloud size={15} /> {uploading ? "Lade hoch…" : "Neues Bild hochladen"}
                    </button>
                </div>
            </div>

            {loading && (
                <div className="bg-white border border-moebus-border rounded-lg p-8 text-center text-slate-500">
                    Lade…
                </div>
            )}

            {!loading && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {/* Original banner card */}
                    <div
                        data-testid="hero-card-original"
                        className={`relative bg-white border rounded-lg overflow-hidden ${
                            !activeId ? "border-emerald-500 ring-2 ring-emerald-200" : "border-moebus-border"
                        }`}
                    >
                        {!activeId && <ActiveBadge />}
                        {originalUrl && (
                            <img src={originalUrl} alt="Original-Banner" className="w-full aspect-[16/9] object-cover" />
                        )}
                        <div className="p-4">
                            <div className="flex items-center gap-1.5 text-sm font-medium text-moebus-deep">
                                <ImageIcon size={14} className="text-moebus-green" /> Original-Banner
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">Immer verfügbar</div>
                            {activeId && (
                                <button
                                    data-testid="activate-original-btn"
                                    onClick={activateOriginal}
                                    className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-moebus-deep hover:bg-moebus-green text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
                                >
                                    <CheckCircle2 size={14} /> Aktivieren
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Uploaded images */}
                    {images.map((img) => {
                        const isActive = img.id === activeId;
                        return (
                            <div
                                key={img.id}
                                data-testid={`hero-card-${img.id}`}
                                className={`relative bg-white border rounded-lg overflow-hidden ${
                                    isActive ? "border-emerald-500 ring-2 ring-emerald-200" : "border-moebus-border"
                                }`}
                            >
                                {isActive && <ActiveBadge />}
                                <img
                                    src={`${BACKEND_BASE}/api/hero-image/${img.id}`}
                                    alt={img.original_filename}
                                    className="w-full aspect-[16/9] object-cover"
                                    loading="lazy"
                                />
                                <div className="p-4">
                                    <div className="text-sm font-medium text-moebus-deep truncate">
                                        {img.original_filename}
                                    </div>
                                    <div className="text-xs text-slate-500 mt-0.5">
                                        Hochgeladen am {formatDate(img.uploaded_at)}
                                    </div>
                                    <div className="flex gap-2 mt-3">
                                        {!isActive && (
                                            <button
                                                data-testid={`activate-image-${img.id}`}
                                                onClick={() => activateImage(img.id)}
                                                className="flex-1 inline-flex items-center justify-center gap-2 bg-moebus-deep hover:bg-moebus-green text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
                                            >
                                                <CheckCircle2 size={14} /> Aktivieren
                                            </button>
                                        )}
                                        <button
                                            data-testid={`delete-image-${img.id}`}
                                            onClick={() => deleteImage(img)}
                                            className={`inline-flex items-center justify-center gap-2 bg-white border border-red-200 hover:bg-red-50 text-red-700 text-sm font-medium px-3 py-2 rounded-md ${
                                                isActive ? "flex-1" : ""
                                            }`}
                                            title="Aus Archiv löschen"
                                        >
                                            <Trash2 size={14} /> {isActive ? "Löschen" : ""}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
